export default {
    name       : 'WarningOperation',
    props : {
        newOperationState : {
            type     : Object,
            required : true
        },
        productMinimums: {
            type: Object,
            required: true
        },
        productMinimumsForTransfer: {
            type: Object,
            required: false,
            default: () => ({ source: {}, target: {} })
        },
        availableShares: {
            type: Number,
            required: false,
        },
        hasOperationsInFlight: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    /**
     * @override
     */
    data() {
        return {
            errorMessage: '',
            limitDate: new Date()
        };
    },
    /**
     * @override
     */
    computed: {
        /**
         * @return {Number} minimal amount
         */
        minSuscription() {
            return this.productMinimums.minimum_investment;
        },
      
        maxSuscription() {
            return this.productMinimums.maximum_investment ? this.productMinimums.maximum_investment : Infinity;
        },  
        /**
         * @return {Number} minimal amount
         */
        minToMantain() {
            return this.productMinimums.minimum_to_maintain;
        },
        isValidPeridicity() {
            if (!this.newOperationState
                || !this.newOperationState.operationObj.per_period
                || !this.newOperationState.operationObj.grossAmount
            ) {
                return;
            }

            const periodicity = this.newOperationState.operationObj.per_period.key;
            const max = this.newOperationState.max_contribution;
            const grossAmount = this.newOperationState.operationObj.grossAmount;
            const anualAportations = {
                'MENSUAL': 12,
                'TRIMESTRAL': 4,
                'SEMESTRAL': 2,
                'ANUAL': 1
            };
            this.newOperationState.operationObj['isValidPeridicity'] = grossAmount > 0 ? grossAmount * anualAportations[periodicity] <= max : true;
            return this.newOperationState.operationObj.isValidPeridicity;
        },
        isValidIncrease() {
            if (!this.newOperationState.operationObj.per_period) {
                return true;
            }

            let increase = this.newOperationState.operationObj.per_increase;

            const grossAmount = this.newOperationState.operationObj.grossAmount;
            const max_limit = this.newOperationState.max_contribution;
            const increase_type = this.newOperationState.operationObj.per_increase_type;
            const periodicity = this.newOperationState.operationObj.per_period.key;
            const anualAportations = {
                'MENSUAL': 12,
                'TRIMESTRAL': 4,
                'SEMESTRAL': 2,
                'ANUAL': 1
            };
            const numberAportations = anualAportations[periodicity];

            if (increase_type === 'PORCENTUAL') {
              increase = (+grossAmount * +increase) / 100;
            }

            const increasedContribution = (+grossAmount + +increase) * numberAportations;
            this.newOperationState.operationObj['isValidIncrease'] = increasedContribution <= max_limit;
            return this.newOperationState.operationObj.isValidIncrease;
        },

        refundError() {
            let isSomeError = false;

            const { amountSharesIndicator, operationObj, productSelected } = this.newOperationState;

            if (!amountSharesIndicator || !operationObj || !productSelected) return false;

            const nav = +productSelected.net_asset_value;
            const amount = amountSharesIndicator.key === 'B' ? +operationObj.grossAmount : +operationObj.netAmount;
            const localShares = operationObj.shares ? +operationObj.shares : amount / nav;
            const isShares = !!operationObj.shares;
            const maxValueGross = this.availableShares * nav;

            let maxShares;

            // En los reembolsos totales no se calcula un máximo a reembolsar
            if (amountSharesIndicator.key === 'T') return false;

            // En el caso de participaciones, se mantiene mínimo el valor de minimum_to_maintain / nav
            if (amountSharesIndicator.key === 'P') {
                maxShares = Math.max(0, this.availableShares);
            }
            // En el caso de importe bruto o neto, se mantiene mínimo el valor de minimum_to_maintain
            else {
                maxShares = Math.max(0, maxValueGross) / nav;
            }

            if (localShares === 0) {
                this.errorMessage = 'El saldo seleccionado no puede ser cero';
                isSomeError = true;
            } else if (this.newOperationState.amountSharesIndicator && this.newOperationState.amountSharesIndicator.key === 'T' &&
                localShares > maxShares) {
                this.errorMessage = 'Participaciones excesivas para realizar un reembolso';
                isSomeError = true;
            } else if (localShares > maxShares && !this.hasOperationsInFlight) {
                this.errorMessage = `El saldo seleccionado es superior al disponible (${isShares ?
                    `${maxShares.toFixed(6)} participaciones` :
                    `${(maxShares * nav).toFixed(2)}€`})`;
                isSomeError = true;
            } else if (localShares > maxShares && this.hasOperationsInFlight) {
                const formattedAmount = isShares 
                    ? `${maxShares.toFixed(6)} participaciones` 
                    : `${(maxShares * nav).toFixed(2)}€`;

                this.errorMessage = `Usted tiene una operación de reembolso en curso. La actual solicitud de reembolso excede las participaciones disponibles descontando las reservadas en la operación en curso. La máxima cantidad para esta solicitud es de ${formattedAmount}.`;
                isSomeError = true;
            }

            return isSomeError;
        },

        /**
         * Comprueba las reglas de un traspaso interno:
         *  1) Orginen: No bajar del mínimo a mantener ni superar el saldo disponible
         *  2) Destino: No bajar del mínimo de inversión ni superar el maximo
         * Respetando operaciones en vuelo
         */ 
        internalTransferError() {
            const { operationObj, amountSharesIndicator } = this.newOperationState;
           
            const nav = operationObj.product_target.net_asset_value;
            const navSource = +this.newOperationState.productSelected.net_asset_value || 1;
            const minToMaintain = +this.minToMantain;                // € mínimo a mantener
            const maxOriginAmt = +this.availableShares * navSource - minToMaintain;  // € máximo transferible
            const shares = +operationObj.shares;  
            const isShares = !!operationObj.shares;
            const amountOrigin = amountSharesIndicator.key === 'P' ? +operationObj.shares * navSource
                : amountSharesIndicator.key === 'B'
                    ? +operationObj.grossAmount
                    : +operationObj.netAmount;
                
            // Validaciones ORIGEN
            if (!operationObj.is_total_transfer) {
                if (amountOrigin === 0 ) {
                    this.errorMessage = `El saldo seleccionado no puede ser cero`;
                    return true;
                }
                
                // No exceder saldo disponible respetando el minimo a mantener
                if (amountOrigin > maxOriginAmt) {
                    if (this.hasOperationsInFlight) {
                        this.errorMessage = `Usted tiene una operación curso. El máximo transferible descontando lo reservado en la operación en curso es de  ${isShares 
                            ? `${(maxOriginAmt / navSource).toFixed(6)} participaciones`
                            : `${maxOriginAmt.toFixed(2)} €`}.`
                    } else {
                        this.errorMessage = `Saldo insuficiente en fondo origen. Máximo transferible: ${ isShares 
                            ? `${(maxOriginAmt / navSource).toFixed(4)} participaciones `
                            : `${maxOriginAmt.toFixed(2)} €.`}`
                    }
                    return true;
                }
            }
        
            // Validaciones DESTINO (si no es traspaso total)
            // Solo validar mínimo, no máximo (en traspasos internos no aplica límite máximo)
            if (!operationObj.is_total_transfer) {
                const minT = +this.productMinimumsForTransfer.target.minimum_investment || 0;
                // const maxT = this.productMinimumsForTransfer.target.maximum_investment;

                if ( amountSharesIndicator.key === 'P') {
                    const minShares = minT / nav;
                    // const maxShares = maxT != null ? maxT / nav : Infinity;

                    if (shares < minShares) {
                        this.errorMessage = `Participaciones inferiores al mínimo a invertir en el fondo destino. Valor mínimo: ${minShares.toFixed(6)} participaciones.`;
                        return true;
                    }
                    // Comentado: En traspasos internos no aplica límite máximo
                    // if (shares > maxShares) {
                    //     this.errorMessage = `Participaciones superiores al máximo permitido en el fondo destino. Valor máximo: ${maxShares.toFixed(6)} participaciones.`;
                    //     return true;
                    // }
                } else {
                    const amt =  amountSharesIndicator.key === 'B' ? +operationObj.grossAmount : +operationObj.netAmount;
                    if (amt < minT) {
                        this.errorMessage = `Importe inferior al mínimo de inversión en el fondo destino. Valor mínimo: ${minT.toFixed(2)} €`;
                        return true;
                    }
                    // Comentado: En traspasos internos no aplica límite máximo
                    // if (maxT != null && amt > maxT) {
                    //     this.errorMessage = `Importe superior al máximo permitido en el fondo destino. Valor máximo: ${maxT.toFixed(2)} €`;
                    //     return true;
                    // }
                }
            }
        
            this.errorMessage = '';
            return false;
        }
    },
    methods: {
        /**
         * Retrieves account operations
         */
        async getAccountOperations() {
            const res = await this.$accountsService.getAccountOperations(this.newOperationState.account.uuid, {});
            this.newOperationState.operationList = res.data.related_operations;
        },

        /**
         * Retrieves the operation created previous to the limitDate
         * @param {*} operations Array sobre el que se busca la operación
         * @return {*} La operación anterior a la fecha límite para retener una participación
         */
        existsOperationBeforeLimitDate(operations) {
            const productsName = [this.newOperationState.productSelected.key_name];

            return operations ?
                operations.filter(op => {
                    // Filtrar los traspasos de clase, ya que no cuentan como operaciones
                    // para el cálculo del reembolso máximo
                    const isClassChange = op.key_operation_type === 'TRASPASO_CLASE' || op.key_sub_operation_type === 'TC';
                    const isFinalized = op.operation_status === 'FINALIZED';

                    if (isFinalized && isClassChange && !productsName.includes(op.key_product)) {
                        productsName.push(op.key_product);
                    }

                    return !isClassChange && isFinalized &&
                        productsName.includes(op.key_product);
                }).find(operation => {
                    let recentDate = new Date(0);
                    const PERIODIC_OPERATION_TYPES = [
                        'SOLICITUD_SUSCRIPCION_PERIODICA',
                        'SOLICITUD_REEMBOLSO_PERIODICO',
                        'SOLICITUD_APORTACION_PERIODICA',
                        'SUSCRIPCION_PERIODICA',
                        'REEMBOLSO_PERIODICO',
                        'APORTACION_PERIODICA'
                    ];
                    const isPeriodic = PERIODIC_OPERATION_TYPES.includes(operation.key_operation_type);
                    if (isPeriodic) {
                        recentDate = operation.per_end_date ? new Date(operation.per_end_date) : new Date();
                    } else {
                        recentDate = operation.operation_date ? new Date(operation.operation_date) : new Date();
                    }

                    return recentDate.getTime() < this.limitDate.getTime();
                }) :
                null;
        }
    },

    /**
     * @override
     */
    beforeMount() {
    },

    async created() {
        try {
            const configResponse = await this.$usersService.getConfigVarByKey('REFUND_LIMIT_DATE', {});
            const dateObject = JSON.parse(configResponse.data.configuration.value);

            await this.getAccountOperations();
            this.limitDate = new Date(dateObject.year, dateObject.month, dateObject.day);
        } catch (err) {
            console.error(err);
        }
    }
};
